import { BaseController } from '@maia/feature-utils';

import {
  ActivityDto,
  ActivityService,
  CompositionsDto,
  DocumentCollectionDto,
  NoteDto,
  NoteService,
  ProductsDto,
  QuotationDto,
  QuotationService,
  TaskDto,
  TaskService,
} from '@maia/shared';

export class QuotationController extends BaseController {
  constructor(
    private quotationService: QuotationService,
    private notesService: NoteService,
    private activityService: ActivityService,
    private tasksService: TaskService,
  ) {
    super();
  }

  async getQuotations(search: Record<string, unknown>) {
    const response = await this.getEntities(
      this.quotationService,
      { path: 'quotations/', search },
      QuotationDto,
    );

    return response;
  }

  async getQuotation(guid: string) {
    const response = await this.getEntity(
      this.quotationService,
      `quotations/${guid}`,
      QuotationDto,
    );
    return response;
  }

  async getProducts(guid: string) {
    const response = await this.quotationService.products(
      `quotations/${guid}/products`,
    );

    return response.map((entity) => new ProductsDto(entity));
  }

  async getCompositions(guid: string) {
    const response = await this.quotationService.compositions(
      `quotations/${guid}/compositions`,
    );
    return response.map((entity) => new CompositionsDto(entity));
  }

  async getActivities(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.activityService,
      { path: `quotations/${guid}/activities`, search },
      ActivityDto,
    );
  }

  async getDocuments(guid: string) {
    const origin = import.meta.env.VITE_NOVULO_URL;
    const response = await this.quotationService.documents(
      `quotations/${guid}/documents`,
    );

    return response.map((entity) => new DocumentCollectionDto(entity, origin));
  }

  async getNotes(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.notesService,
      { path: `quotations/${guid}/notes`, search },
      NoteDto,
    );
  }

  async createNote(
    guids: {
      customerGuid: string;
      quotationGuid: string;
    },
    params: Parameters<typeof this.notesService.create>[1],
  ) {
    return await this.notesService.create(
      `customers/${guids.customerGuid}/quotations/${guids.quotationGuid}/notes`,
      params,
    );
  }

  async getTasks(guid: string, search: Record<string, unknown>) {
    return this.getEntities(
      this.tasksService,
      { path: `quotations/${guid}/tasks`, search },
      TaskDto,
    );
  }

  async cancel(guid: { customerGuid: string; quotationGuid: string }) {
    return await this.quotationService.cancel(
      `customers/${guid.customerGuid}/quotations/${guid.quotationGuid}/cancel`,
    );
  }

  async remove(guid: { customerGuid: string; quotationGuid: string }) {
    return await this.quotationService.remove(
      `customers/${guid.customerGuid}/quotations/${guid.quotationGuid}`,
    );
  }

  async sendMail(guid: { customerGuid: string; quotationGuid: string }) {
    return await this.quotationService.sendMail(
      `customers/${guid.customerGuid}/quotations/${guid.quotationGuid}/send-email`,
    );
  }
}
